import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { Vote, Mail, Lock, ArrowRight, Eye, EyeOff, AlertTriangle } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { LanguageSelector } from "../components/LanguageSelector";
import { toast } from "sonner";
import { login } from "../lib/api";
import api from "../lib/api";

import { useAppConfig } from "../lib/AppConfigContext";

export default function LoginPage() {
  const { appName } = useAppConfig();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [demoLoading, setDemoLoading] = useState(false);
  const [lockedInfo, setLockedInfo] = useState(null);

  const handleDemoLogin = async () => {
    setDemoLoading(true);
    try {
      const response = await api.post("/entities/demo-login");
      localStorage.setItem("entity_id", response.data.entity_id);
      localStorage.setItem("entity_name", response.data.entity_name);
      localStorage.setItem("is_demo", "true");
      if (response.data.logo_url) {
        localStorage.setItem("entity_logo", response.data.logo_url);
      }
      if (response.data.language) {
        localStorage.setItem("entity_language", response.data.language);
        i18n.changeLanguage(response.data.language);
      }
      navigate("/dashboard");
    } catch {
      toast.error(t('errors.generic'));
    } finally {
      setDemoLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await login(email, password);
      localStorage.setItem("entity_id", response.data.entity_id);
      localStorage.setItem("entity_name", response.data.entity_name);
      if (response.data.logo_url) {
        localStorage.setItem("entity_logo", response.data.logo_url);
      }
      if (response.data.language) {
        localStorage.setItem("entity_language", response.data.language);
        i18n.changeLanguage(response.data.language);
      }
      toast.success(t('common.success'));
      if (response.data.needs_key_generation) {
        navigate("/generate-keys");
      } else {
        navigate("/dashboard");
      }
    } catch (error) {
      const detail = error.response?.data?.detail || "";
      if (error.response?.status === 423 && detail.startsWith("account_locked")) {
        const minutes = detail.split("|")[1] || "15";
        setLockedInfo(minutes);
      } else if (detail === "email_not_verified") {
        toast.error(t('register.emailNotVerified'));
      } else {
        toast.error(detail || t('errors.invalidCredentials'));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Language selector */}
      <div className="absolute top-4 right-4 z-50">
        <LanguageSelector />
      </div>
      
      {/* Left side - Image */}
      <div className="hidden lg:flex lg:w-1/2 relative">
        <img
          src="https://images.unsplash.com/photo-1647705985473-968ff327b7a8?auto=format&fit=crop&w=2000&q=80"
          alt={t('voting.title')}
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-slate-900/60" />
        <div className="relative z-10 flex flex-col justify-end p-12 text-white">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <h2 className="text-4xl font-heading font-bold mb-4">
              {t('common.secureVotingSystem')}
            </h2>
            <p className="text-lg text-slate-300 max-w-md">
              {t('auth.registerSubtitle')}
            </p>
          </motion.div>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-slate-50">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md"
        >
          {/* Logo */}
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 bg-slate-900 rounded-xl flex items-center justify-center">
              <Vote className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className="font-heading font-bold text-xl text-slate-900">{appName}</h1>
              <p className="text-sm text-slate-500">{t('consulta.title')}</p>
            </div>
          </div>

          {/* Form */}
          <div className="bg-white rounded-xl border border-slate-200 p-8 shadow-sm">
            <h2 className="text-2xl font-heading font-bold text-slate-900 mb-2">
              {t('auth.loginTitle')}
            </h2>
            <p className="text-slate-500 mb-6">
              {t('auth.loginSubtitle')}
            </p>

            <form onSubmit={handleSubmit} className="space-y-5">
              {lockedInfo && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4" data-testid="account-locked-banner">
                  <div className="flex gap-3">
                    <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-red-800 mb-1">{t('errors.accountLockedTitle')}</p>
                      <p className="text-sm text-red-700 mb-3">{t('errors.accountLocked', { minutes: lockedInfo })}</p>
                      <p className="text-sm text-red-700 mb-2">{t('errors.accountLockedSuggest')}</p>
                      <Link
                        to="/forgot-password"
                        className="inline-flex items-center gap-1.5 text-sm font-medium text-white bg-red-600 hover:bg-red-700 px-4 py-2 rounded-md transition-colors"
                        data-testid="locked-recover-link"
                      >
                        <Lock className="h-3.5 w-3.5" />
                        {t('errors.recoverPassword')}
                      </Link>
                    </div>
                  </div>
                </div>
              )}
              {!lockedInfo && (
              <>
              <div className="form-group">
                <Label htmlFor="email" className="form-label">
                  {t('auth.email')}
                </Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10 h-11"
                    placeholder={t('auth.emailPlaceholder')}
                    required
                    data-testid="login-email"
                  />
                </div>
              </div>

              <div className="form-group">
                <Label htmlFor="password" className="form-label">
                  {t('auth.password')}
                </Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10 pr-10 h-11"
                    placeholder="••••••••"
                    required
                    data-testid="login-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    data-testid="toggle-password"
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>

              <div className="flex justify-end">
                <Link
                  to="/forgot-password"
                  className="text-sm text-blue-600 hover:text-blue-700"
                  data-testid="forgot-password-link"
                >
                  {t('auth.forgotPassword')}
                </Link>
              </div>

              <Button
                type="submit"
                className="w-full h-11 bg-slate-900 hover:bg-slate-800 btn-hover"
                disabled={loading}
                data-testid="login-submit"
              >
                {loading ? (
                  <div className="spinner" />
                ) : (
                  <>
                    {t('auth.login')}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </>
                )}
              </Button>
              </>
              )}
            </form>
          </div>

          <p className="text-center mt-6 text-slate-600">
            {t('auth.noAccount')}{" "}
            <Link
              to="/register"
              className="text-blue-600 hover:text-blue-700 font-medium"
              data-testid="register-link"
            >
              {t('auth.register')}
            </Link>
          </p>

          {/* Demo button */}
          <div className="mt-6 pt-6 border-t border-slate-200">
            <Button
              type="button"
              variant="outline"
              className="w-full h-11 border-amber-400 text-amber-700 hover:bg-amber-50 hover:border-amber-500"
              disabled={demoLoading}
              onClick={handleDemoLogin}
              data-testid="demo-login-btn"
            >
              {demoLoading ? (
                <div className="spinner" />
              ) : (
                <>
                  <Eye className="mr-2 h-4 w-4" />
                  {t('demo.demoButton')}
                </>
              )}
            </Button>
            <p className="text-center text-xs text-slate-400 mt-2">
              {t('demo.demoSubtitle')}
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
